import type {ImportStart, SharedData} from '@gaia/types';
import {osmIdSchema} from '@gaia/types/schemas/osmIdSchema';
import {osmSelectionStateSchema} from '@gaia/types/schemas/osmSelectionStateSchema';
import {isValidLat, isValidLng, isValidZoom} from '@gaia/utils/gaia/isValidCoordinates';
import {lineStringToPolyline, polylineToLineString} from '@gaia/utils/lineStringPolyline';
import {LngLat} from 'mapbox-gl';
import qs, {type ParsedQs} from 'qs';

export interface UrlProps {
    layers?: string[];
    center?: mapboxgl.LngLat;
    zoom?: number;
    sharedData?: SharedData;
    sidebar?: SidebarState;
    selections?: SelectionState[];
    customSource?: string;
    showFreeTrialOffer?: boolean;
    partner?: string;
    importState?: ImportStart;
}

export function stringifyUrlProps({
    sidebar,
    layers,
    zoom,
    center,
    sharedData,
    selections = [],
}: UrlProps): string {
    const queryString = qs.stringify(
        {
            ...qs.parse(location.search.slice(1)),

            loc:
                zoom && center
                    ? zoom.toFixed(1) + '/' + center.lng.toFixed(4) + '/' + center.lat.toFixed(4)
                    : undefined,

            layer: layers && layers.length ? layers.slice(0).reverse() : undefined,

            sidebar: sidebar && sidebar.type === 'print' ? 'print' : undefined,

            osmData:
                selections.length === 1 && selections[0].type === 'osm'
                    ? encodeOSMData(selections[0])
                    : undefined,

            knownRouteId:
                selections.length === 1 && selections[0].type === 'knownRoute'
                    ? selections[0].knownRouteId
                    : undefined,

            pubLink:
                sharedData &&
                sharedData.type !== 'geometry' &&
                sharedData.type !== 'FeatureCollection' &&
                sharedData.pubLink
                    ? sharedData.pubLink
                    : undefined,

            trackId: sharedData && sharedData.type === 'track' ? sharedData.id : undefined,

            areaId: sharedData && sharedData.type === 'area' ? sharedData.id : undefined,

            waypointId: sharedData && sharedData.type === 'waypoint' ? sharedData.id : undefined,

            folderId: sharedData && sharedData.type === 'folder' ? sharedData.id : undefined,
        },
        {arrayFormat: 'comma', encode: false}
    );

    return queryString ? `?${queryString}` : '';
}

function getPubLink(input: ParsedQs): string | undefined {
    return typeof input.pubLink === 'string' ? input.pubLink : undefined;
}

export function parseUrlProps(inputString: string): UrlProps {
    const input = qs.parse(inputString.slice(1));
    const output: UrlProps = {};

    if (typeof input.loc === 'string') {
        // `&loc=[z]/[lng]/[lat]`

        const [zoom, lng, lat]: unknown[] = input.loc.split('/').map((s) => parseFloat(s));
        if (isValidLng(lng) && isValidLat(lat)) output.center = new LngLat(lng, lat);
        if (isValidZoom(zoom)) output.zoom = zoom;
    } else if (typeof input.lon === 'string' && typeof input.lat === 'string') {
        // `&lon=[x]&lat=[y]&zoom=[z]`

        const lng = parseFloat(input.lon);
        const lat = parseFloat(input.lat);

        if (isValidLng(lng) && isValidLat(lat)) {
            output.center = new LngLat(lng, lat);
        }

        if (typeof input.zoom === 'string') {
            const zoom = parseFloat(input.zoom);
            if (isValidZoom(zoom)) output.zoom = zoom;
        }
    }

    if (typeof input.waypointId === 'string') {
        output.sharedData = {
            type: 'waypoint',
            id: input.waypointId,
            pubLink: getPubLink(input),
        };
        output.selections = [
            {
                type: 'waypoint',
                localId: input.waypointId,
            },
        ];
        output.sidebar = {
            type: 'waypointDetails',
            id: input.waypointId,
        };
    } else if (typeof input.folderId === 'string') {
        output.sharedData = {
            type: 'folder',
            id: input.folderId,
            pubLink: getPubLink(input),
        };
        output.sidebar = {
            type: 'folderDetails',
            id: input.folderId,
            prevSidebar: {type: 'hidden'},
        };
    } else if (typeof input.trackId === 'string') {
        output.sharedData = {
            type: 'track',
            id: input.trackId,
            pubLink: getPubLink(input),
        };
        output.sidebar = {type: 'trackDetails', id: input.trackId};
        output.selections = [
            {
                type: 'track',
                localId: input.trackId,
            },
        ];
    } else if (typeof input.areaId === 'string') {
        output.sharedData = {
            type: 'area',
            id: input.areaId,
            pubLink: getPubLink(input),
        };
        output.selections = [
            {
                type: 'area',
                localId: input.areaId,
            },
        ];
        output.sidebar = {type: 'areaDetails', id: input.areaId};
    } else if (typeof input.knownRouteId === 'string') {
        const knownRouteId = parseInt(input.knownRouteId, 10);

        if (osmIdSchema.safeParse(knownRouteId).success) {
            output.selections = [
                {
                    type: 'knownRoute',
                    knownRouteId,
                },
            ];

            output.sidebar = {type: 'knownRoute'};
        }
    }

    switch (input.sidebar as SidebarState['type'] | 'import') {
        case 'savedItems': {
            output.sidebar = {type: 'savedItems'};
            break;
        }
        case 'print': {
            output.sidebar = {type: 'print'};
            break;
        }
        case 'layers': {
            output.sidebar = {type: 'layers'};
            break;
        }
        case 'customSource': {
            output.sidebar = {type: 'customSource'};
            break;
        }
        case 'import': {
            // The Import sidebar is a descendent of the Saved Items sidebar. When the <Map> opens
            // the sidebar based on the URL query parameter, it needs extra data to know if it
            // should show the Import sidebar or the  actual Saved Items sidebar, so we add
            // `output.importState` to let it know.
            if (window.location.pathname === '/map/') {
                output.sidebar = {type: 'savedItems'};
                output.importState = {status: 'sidebarOpen'};
            }
            break;
        }
    }

    if (typeof input.layer === 'string') {
        output.layers = input.layer.split(',').reverse();
    }

    if (typeof input.osmData === 'string') {
        const osmData = decodeOSMData(input.osmData);
        if (osmData) {
            output.sidebar = {type: 'selection'};
            output.selections = [osmData];
        }
    }

    if (input.offer === 'freetrial' && window.location.pathname === '/map/') {
        output.showFreeTrialOffer = true;
    }

    if (typeof input.partner === 'string') {
        output.partner = input.partner;
    }

    return output;
}

function encodeOSMData(osmData: OSMSelectionState): string | undefined {
    let geometry: OSMGeometry | undefined = undefined;

    switch (osmData.properties.geometry?.type as OSMGeometryType | undefined) {
        case 'LineString':
        case 'MultiLineString':
            geometry = lineStringToPolyline(osmData.properties.geometry);
            break;
        case 'Point':
            geometry = osmData.properties.geometry;
            break;
    }

    if (geometry === undefined) return;

    const stringifiedOsmData = JSON.stringify({
        ...osmData,
        properties: {...osmData.properties, geometry},
    });

    return encodeURIComponent(stringifiedOsmData);
}

function decodeOSMData(encodedString: string) {
    try {
        const osmSelection = osmSelectionStateSchema.parse(
            JSON.parse(decodeURIComponent(encodedString))
        );

        const osmType = osmSelection.properties?.geometry?.type;

        if (osmType === 'LineString' || osmType === 'MultiLineString') {
            osmSelection.properties.geometry = polylineToLineString(
                osmSelection.properties.geometry
            );
        }

        return osmSelection;
    } catch (e) {
        console.error(e);
        return;
    }
}
